<template>
    <div class="video-widget" :class="{ 'video-widget--fullscreen': state.isFullscreen }">
        <!-- Thumbnail/Preview -->
        <div v-if="!state.isVisible && hasStories" class="video-widget__preview"
            :class="{ 'loading': state.isLoading, 'dragging': ui.isDragging }" :style="thumbnailStyles"
            @mousedown="startDrag" @touchstart="startDrag">
            <div class="video-widget__thumbnail">
                <div v-if="firstStory?.videos?.[0]" class="video-widget__thumbnail-media"
                    :style="{ backgroundImage: `url(${getVideoPoster(firstStory.videos[0])})` }">
                    <video :src="getVideoUrl(firstStory.videos[0])" :poster="getVideoPoster(firstStory.videos[0])" muted
                        loop autoplay playsinline preload="metadata">
                    </video>
                </div>
                <div v-if="state.isLoading" class="video-widget__spinner"></div>
            </div>
            <div class="video-widget__thumbnail-description"
                v-if="firstStory && firstStory.description && firstStory.description != ''"
                :class="{ 'video-widget__thumbnail-description--animate': !hasAnimated }">
                <span class="video-widget__thumbnail-description-text">{{ firstStory.description }}</span>
            </div>
        </div>

        <!-- Shared Video Player Modal -->
        <PlayerModal v-if="state.isVisible" :is-visible="state.isVisible" :stories="props.stories"
            :autoplay="props.autoplay" :muted="player.isMuted" :show-controls="props.showControls"
            :analytics="props.analytics" @player-close="closePlayer"
            @video-start="(story, data) => emit('video-start', story, data)"
            @video-end="(data) => emit('video-end', data)" @story-change="(story) => emit('story-change', story)"
            @video-progress="(data) => emit('video-progress', data)"
            @video-interaction="(data) => emit('video-interaction', data)" />

        <div v-if="keyboardFeedback.showFeedback" class="video-widget__keyboard-feedback">
            {{ keyboardFeedback.lastKey }}
        </div>

        <!-- Loading Overlay -->
        <div v-if="state.isLoading && state.isVisible" class="video-widget__loading">
            <div class="video-widget__spinner"></div>
        </div>
    </div>
</template>

<script setup>
import {
    ref,
    reactive,
    computed,
    onMounted,
    onBeforeUnmount,
    nextTick,
    watch
} from 'vue'
import PlayerModal from './PlayerModal.vue'

// Props
const props = defineProps({
    stories: {
        type: Array,
        default: () => []
    },
    autoplay: {
        type: Boolean,
        default: true
    },
    muted: {
        type: Boolean,
        default: true
    },
    showControls: {
        type: Boolean,
        default: true
    },
    analytics: {
        type: Object,
        default: null
    }
})

// Emits
const emit = defineEmits([
    'video-start',
    'video-end',
    'story-change',
    'video-progress',
    'player-open',
    'player-close',
    'video-interaction'
])

// Refs
const hasAnimated = ref(false)
const showDescriptionAnimation = ref(false)

// State
const state = reactive({
    isVisible: false,
    isLoading: false,
    isFullscreen: false,
    error: null,
    initialized: false
})

const ui = reactive({
    isDragging: false,
    dragOffset: { x: 0, y: 0 },
    thumbnailPosition: { x: 20, y: 80 },
    showControls: true,
    fullscreen: false,
    dragStartPos: { x: 0, y: 0 },
    dragThreshold: 5,
    dragDistance: 0,
    clickStartTime: 0,
    maxClickDuration: 300,
    recentDrag: false
})

const player = reactive({
    isMuted: props.muted
})

const keyboardFeedback = reactive({
    lastKey: null,
    showFeedback: false,
    feedbackTimeout: null
})

// Computed
const hasStories = computed(() => props.stories?.length > 0)
const firstStory = computed(() => props.stories?.[0])

const thumbnailStyles = computed(() => {
    return {
        left: `${ui.thumbnailPosition.x}px`,
        bottom: `${ui.thumbnailPosition.y}px`,
        cursor: ui.isDragging ? 'grabbing' : 'grab'
    }
})

// Methods
const getVideoUrl = (videoData) => {
    if (!videoData) return ''
    if (typeof videoData === 'string') return videoData
    return videoData.url || videoData.attachment || ''
}

const getVideoPoster = (videoData) => {
    if (!videoData) return ''
    if (typeof videoData === 'object') return videoData.thumbnail || videoData.poster || ''
    return ''
}

// Drag functionality
const startDrag = (event) => {
    ui.clickStartTime = Date.now()
    ui.dragDistance = 0
    ui.isDragging = false
    const clientX = event.type === 'touchstart' ? event.touches[0].clientX : event.clientX
    const clientY = event.type === 'touchstart' ? event.touches[0].clientY : event.clientY
    ui.dragStartPos = { x: clientX, y: clientY }
    if (event.type === 'touchstart') {
        document.addEventListener('touchmove', onDrag, { passive: false })
        document.addEventListener('touchend', stopDrag)
    } else {
        document.addEventListener('mousemove', onDrag)
        document.addEventListener('mouseup', stopDrag)
    }
    if (event.type === 'touchstart') event.preventDefault()
}

const onDrag = (event) => {
    const clientX = event.type === 'touchmove' ? event.touches[0].clientX : event.clientX
    const clientY = event.type === 'touchmove' ? event.touches[0].clientY : event.clientY
    const deltaX = clientX - ui.dragStartPos.x
    const deltaY = clientY - ui.dragStartPos.y
    ui.dragDistance = Math.sqrt(deltaX * deltaX + deltaY * deltaY)
    if (ui.dragDistance > 3 && !ui.isDragging) {
        ui.isDragging = true
        event.preventDefault()
    }
    if (ui.isDragging) {
        event.preventDefault()
        const newX = ui.thumbnailPosition.x + deltaX
        const newY = ui.thumbnailPosition.y - deltaY
        const thumbnailWidth = 90
        const thumbnailHeight = 126
        const margin = 10
        const minX = margin
        const maxX = window.innerWidth - thumbnailWidth - margin
        const minY = margin
        const maxY = window.innerHeight - thumbnailHeight - margin
        ui.thumbnailPosition.x = Math.max(minX, Math.min(maxX, newX))
        ui.thumbnailPosition.y = Math.max(minY, Math.min(maxY, newY))
        ui.dragStartPos = { x: clientX, y: clientY }
    }
}

const stopDrag = (event) => {
    const clickDuration = Date.now() - ui.clickStartTime
    const isClick = !ui.isDragging && ui.dragDistance <= ui.dragThreshold && clickDuration <= ui.maxClickDuration
    document.removeEventListener('mousemove', onDrag)
    document.removeEventListener('mouseup', stopDrag)
    document.removeEventListener('touchmove', onDrag, { passive: false })
    document.removeEventListener('touchend', stopDrag)
    if (isClick) openPlayer()
    else {
        saveThumbnailPosition()
        event.preventDefault()
        event.stopPropagation()
    }
    ui.isDragging = false
    ui.dragDistance = 0
}

const saveThumbnailPosition = () => {
    localStorage.setItem('video-widget-thumbnail-position', JSON.stringify(ui.thumbnailPosition))
}

const loadThumbnailPosition = () => {
    const saved = localStorage.getItem('video-widget-thumbnail-position')
    if (saved) {
        try {
            const position = JSON.parse(saved)
            const margin = 10
            ui.thumbnailPosition.x = Math.max(margin, Math.min(window.innerWidth - 100, position.x))
            ui.thumbnailPosition.y = Math.max(margin, Math.min(window.innerHeight - 136, position.y))
        } catch (e) { }
    }
}

const openPlayer = () => {
    if (!hasStories.value) return
    if (!hasAnimated.value) hasAnimated.value = true
    state.isVisible = true
    if (props.analytics) props.analytics.trackPlayerOpen()
    emit('player-open')
}

const closePlayer = () => {
    if (props.analytics) props.analytics.trackPlayerClose()
    emit('player-close')
    state.isVisible = false
}

watch(firstStory, (newStory) => {
    if (newStory && newStory.description) showDescriptionAnimation.value = true
}, { immediate: true })

onMounted(() => {
    loadThumbnailPosition()
    if (props.analytics && hasStories.value) {
        props.analytics.trackWidgetLoad({
            storiesCount: props.stories.length,
            loadTime: 0,
            position: 'floating',
            theme: 'default'
        })
    }
})

defineExpose({
    openPlayer,
    closePlayer
})
</script>